// Single source of truth for ALL editable site content — text, images, video.
//
// The public site reads from here via useContent(); the /admin backpanel
// writes to here. Persisted to localStorage so it survives reloads with no
// backend. Media (logo/photos/video) are stored as data-URL strings so they
// live in localStorage too — no server upload needed.
//
// Shape: a single nested object. Defaults below mirror exactly what ships in
// the code today, so an un-edited site looks identical.

import { useEffect, useState } from "react";
import { IMG } from "./placeholders";

const KEY = "nr_site_content_v4"; // v4: Dapoli project corrected to Seaview Enclaves

// ---------------------------------------------------------------------------
// DEFAULTS — current site content
// ---------------------------------------------------------------------------
export const DEFAULT_CONTENT = {
  // Shared business details — used in nav, footer, contact page, home phases.
  settings: {
    brandName: "Nausiddhi Realty",
    brandTagline: "The New Standard",
    phone: "+91 70090 05400",
    phoneRaw: "+917009005400",
    whatsappRaw: "917009005400",
    email: "info@nausiddhirealty.com",
    instagram: "https://www.instagram.com/nausiddhi_realty",
    linkedin: "https://www.linkedin.com/company/nausiddhi/",
    facebook: "https://www.facebook.com/share/17XrNQgz8u/",
    rera: "MahaRERA/A52100012345",
    // Logos (paths or data URLs). Defaults point at the bundled files.
    logoMark: "/brand/logo-nav.png",
    logoMedium: "/brand/logo-full.png",
    office: {
      line1: "Kohinoor World Towers, T3",
      line2: "3rd Floor, Office No. 308–309",
      line3: "Opp. Empire Estate, Pimpri-Chinchwad",
      line4: "Pune – 411018, Maharashtra",
      hours: "Mon–Sat · 10:00 – 18:00 IST (GMT +5:30)",
      mapQuery: "Kohinoor+World+Towers+Pimpri-Chinchwad+Pune",
      coordinates: "18.6298° N · 73.8474° E",
      cityLabel: "Pimpri-Chinchwad, Pune",
    },
  },

  // Footer (mostly pulls from settings; brandLine is its own copy).
  footer: {
    brandLine: "Premium plotted projects across Maharashtra - Mangaon, Shrivardhan, Dapoli, and Shirdi.",
  },

  // About page.
  about: {
    metaTitle: "About - Nausiddhi Realty",
    metaDescription: "Nausiddhi Realty is a Pune-based developer of NA-approved plotted projects in Mangaon, Shrivardhan, Dapoli and Shirdi - clear titles, complete infrastructure, registered possession.",
    heroEyebrow: "Who we are",
    heroTitle: "Nausiddhi",
    heroTitleEm: "Realty",
    heroSub: "A Pune-based real estate company delivering plotted projects across Maharashtra's most promising growth corridors.",
    heroLocations: "Mangaon · Raigad, Shrivardhan, Dapoli, Shirdi",
    heroImage: IMG.aboutHero,
    heroCaption: "Nausiddhi Realty · Maharashtra",
    principlesEyebrow: "How we work",
    principlesTitle: "Three things every project must get right.",
    principlesLede: "These decide which land we buy and how we hand it over. A parcel that can't clear all three never carries our name.",
    pillars: [
      { n: "01", title: "Clean title, verified", body: "Every parcel is NA-approved freehold land, verified by our legal team before we acquire it. The plot is registered in your name on the day of booking - with the full title chain available for your lawyer to review." },
      { n: "02", title: "Infrastructure before possession", body: "Compound walls, internal roads, water lines, electricity and drainage are completed before handover - not promised after. Your plot is ready to build on from day one." },
      { n: "03", title: "Locations chosen for growth", body: "Mangaon along the Mumbai–Goa corridor, Shrivardhan and Dapoli on the Konkan coast, Sai Srushti minutes from Shirdi. We buy where infrastructure and demand are headed, not where they've already peaked." },
    ],
    numbers: [
      { v: "350+", label: "Acres across projects" },
      { v: "4", label: "Active projects" },
      { v: "5,250+", label: "Total plots" },
      { v: "4", label: "Locations" },
    ],
    storyImage: IMG.story,
    storyImageCaption: "Mangaon · Raigad district",
    storyEyebrow: "Our story",
    storyTitle: "Built from Pune. Delivered across Maharashtra.",
    storyPara1: "Nausiddhi Realty was founded on a straightforward conviction - that land in the right location, with clear title and finished infrastructure, holds value across generations. From our office in Pimpri-Chinchwad, Pune, we identify, develop and deliver plotted projects along Maharashtra's growth corridors.",
    storyPara2: "Today we manage four active projects - Nausiddhi Residency in Mangaon, BlueRain Resort Homes in Shrivardhan, Seaview Enclaves in Dapoli, and Sai Srushti near Shirdi - spanning over 350 acres and 5,250+ plots. Buyers from across India and overseas hold land with us; every plot is handed over with clear title, complete infrastructure, and registration in the owner's name.",
    visionImage: IMG.vision,
    visionEyebrow: "Our vision",
    visionTitleLine1: "Building tomorrow,",
    visionTitleLine2: "rooted in nature.",
    visionLine: "Every acre is thoughtfully planned to create a lifestyle that blends modern living with timeless natural beauty.",
    peopleEyebrow: "The people",
    peopleTitle: "A small, deliberate team.",
    people: [
      { role: "Founder & Director", line: "Leads the vision, land acquisition strategy, and overall direction of Nausiddhi Realty.", photo: IMG.people[0] },
      { role: "Sales & Operations", line: "Manages client relationships, reservations, and end-to-end project coordination.", photo: IMG.people[1] },
      { role: "Legal & Documentation", line: "Oversees title verification, NA approvals, and registration across all four project locations.", photo: IMG.people[2] },
    ],
    ctaTitle: "See the land before you decide.",
    ctaLine: "Book a site visit - or a video walkthrough if you're outside India. We share the title documents, approvals and master plan before you commit to anything.",
    ctaButton: "Plan a visit",
    ctaImage: IMG.cta,
  },

  // Contact page (contact details themselves live in settings).
  contact: {
    metaTitle: "Contact - Nausiddhi Realty",
    metaDescription: "Speak with the Nausiddhi Realty concierge about a private viewing, plot reservation, or build.",
    heroEyebrow: "Get in touch",
    heroTitle: "Let's talk",
    heroTitleEm: "land.",
    heroImage: IMG.contactHero,
    heroCaption: "Pimpri-Chinchwad · Pune",
    interests: "A private viewing, Plot reservation, Build & architecture, General enquiry",
    nriTitle: "NRI & International Buyers",
    nriLine: "We work with buyers across the Gulf, US, UK, Singapore and beyond. Video site walkthroughs, document review over email, and registration support via Power of Attorney - all coordinated on your time zone over WhatsApp.",
  },

  // Projects listing page hero (cards come from projectsStore).
  projects: {
    metaTitle: "Projects - Nausiddhi Realty",
    metaDescription: "Four NA-approved plotted projects across Maharashtra - Nausiddhi Residency (Mangaon), BlueRain Resort Homes (Shrivardhan), Seaview Enclaves (Dapoli), Sai Srushti (Shirdi). 5,250+ plots, clear titles.",
    heroEyebrow: "Our portfolio",
    heroLocations: "Mangaon · Raigad, Shrivardhan, Dapoli, Shirdi",
    heroImage: IMG.projectsHero,
    heroCaption: "Maharashtra · India",
    bandEyebrow: "Have a specific brief?",
    bandTitle: "Tell us what you're looking for.",
    bandLine: "Plot size, budget, sea-facing, near Shirdi - share your requirement and we'll match you to the right parcel across our four projects, including phases not yet listed on this page.",
    bandButton: "Share your brief",
  },

  // Home cinematic experience overlays.
  home: {
    // PLOT phase reveal title + master-plan hint card.
    plotRevealOverline: "Nausiddhi Realty presents",
    plotRevealTitle: "Nausiddhi",
    plotRevealSub: "A guided tour of the estate",
    plotHintLabel: "Master Plan",
    plotHintText: "Drag · Pinch to zoom · Tap for details",
    // MAP phase HUD. The 5 map pins themselves are fixed in MapPhase.jsx.
    mapHudNum: "02 / Setting",
    mapHudText: "Scroll to descend",
    // DAY AT NAUSIDDHI phase — the sunrise-to-night ground scene.
    dayScrollCue: "Scroll to begin the day",
    dayHeroLine1: "Every great journey begins",
    dayHeroLine2: "with a place that feels like",
    dayHeroEmphasis: "home.",
    dayCaptions: [
      "Live close to nature",
      "Designed for families",
      "Breathe fresh, every day",
    ],
    dayAmenities: [
      "The Clubhouse",
      "Open lawns & seating",
      "Meditation garden",
      "Reflecting waters",
    ],
    dayFinalLine1: "Not just a plot.",
    dayFinalEmphasis: "A place to belong.",
    dayFinalEyebrow: "Experience Nausiddhi",
    dayCtaText: "Reserve your dream today",
    // CONSTRUCTION phase — the build storyboard.
    constructionVideo: "", // optional video (currently a webp frame sequence)
    constructionLabel: "04 / Possibilities",
    stages: [
      { label: "Boundary Wall", desc: "Compound wall, monsoon-grade", h1: "From open land", h2: "to your plot.", body: "Every Nausiddhi plot begins with a clear boundary. Reinforced compound walls go up first - marking the land that's yours, built to outlast generations.", statValue: "5,250+", statCaption: "Plots across 4 projects" },
      { label: "Internal Roads", desc: "All-weather paved access", h1: "Every plot,", h2: "reachable.", body: "Wide internal roads connect every plot to the entrance gate. Paved, drained, and designed for year-round access regardless of monsoon.", statValue: "200 Acres", statCaption: "Nausiddhi Residency, Mangaon" },
      { label: "Utilities", desc: "Water, power, sewage", h1: "Infrastructure", h2: "built-in.", body: "Underground water supply lines, electricity connections, drainage and sewage - all laid before handover. Your plot is ready to build on from day one.", statValue: "Day 1", statCaption: "Infrastructure ready on possession" },
      { label: "Landscaping", desc: "Streets, lights, greenery", h1: "A community,", h2: "not just land.", body: "Street lights along every road, CCTV at the entrance, manicured landscaping throughout. The site feels like a neighbourhood before the first home is built.", statValue: "24/7", statCaption: "CCTV monitored entrance" },
      { label: "Possession", desc: "Registered, yours", h1: "Your land.", h2: "Your future.", body: "NA approved. Clear freehold title. Registered in your name the day of booking. Four locations, one standard - Nausiddhi.", statValue: "NA", statCaption: "Non-agricultural approved" },
    ],
    // RESERVATION phase — closing CTA.
    resLabel: "05 / Reserve",
    resCount: 5250,
    resCountSub: "plots across 4 projects.",
    resBody: "Four active projects across Maharashtra - Mangaon, Shrivardhan, Dapoli, and Shirdi. NA approved, clear title, full infrastructure. Viewings open now.",
    resButton: "Enquire Now",
    resFooter: "Nausiddhi Realty Pvt. Ltd. · Pune, Maharashtra",
  },
};

// ---------------------------------------------------------------------------
// Persistence + deep merge (so new default keys appear after upgrades)
// ---------------------------------------------------------------------------
function isObj(v) {
  return v && typeof v === "object" && !Array.isArray(v);
}

function deepMerge(base, override) {
  if (!isObj(base)) return override === undefined ? base : override;
  const out = Array.isArray(base) ? [...base] : { ...base };
  if (!isObj(override)) return out;
  for (const k of Object.keys(override)) {
    out[k] = isObj(base[k]) ? deepMerge(base[k], override[k]) : override[k];
  }
  return out;
}

let cache = null;

function load() {
  if (cache) return cache;
  let stored = null;
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) stored = JSON.parse(raw);
  } catch {
    stored = null;
  }
  cache = stored ? deepMerge(DEFAULT_CONTENT, stored) : DEFAULT_CONTENT;
  return cache;
}

function persist(next) {
  cache = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new CustomEvent("nr-content-change"));
  } catch {
    /* storage full/unavailable */
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export function getContent() {
  return load();
}

// Read a value at a dotted path, e.g. get("settings.phone").
export function get(path, fallback = "") {
  const parts = path.split(".");
  let cur = load();
  for (const p of parts) {
    if (cur == null) return fallback;
    cur = cur[p];
  }
  return cur === undefined ? fallback : cur;
}

// Write a value at a dotted path and persist. Returns the new content.
export function set(path, value) {
  const parts = path.split(".");
  const root = deepMerge(load(), {}); // clone
  let cur = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i];
    cur[k] = isObj(cur[k]) ? { ...cur[k] } : Array.isArray(cur[k]) ? [...cur[k]] : {};
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
  persist(root);
  return root;
}

// Replace the whole content object (used by import).
export function replaceAll(next) {
  persist(deepMerge(DEFAULT_CONTENT, next));
}

export function resetContent() {
  persist(DEFAULT_CONTENT);
}

export function subscribeContent(cb) {
  const local = () => cb();
  const cross = (e) => { if (e.key === KEY) { cache = null; cb(); } };
  window.addEventListener("nr-content-change", local);
  window.addEventListener("storage", cross);
  return () => {
    window.removeEventListener("nr-content-change", local);
    window.removeEventListener("storage", cross);
  };
}

// React hook: returns the whole content object and re-renders on any change.
export function useContent() {
  const [, force] = useState(0);
  useEffect(() => subscribeContent(() => force((n) => n + 1)), []);
  return getContent();
}

// Split a comma-separated field into a trimmed array.
export function commaList(text) {
  return String(text || "").split(",").map((s) => s.trim()).filter(Boolean);
}
